/**
 * useShellQuietMoves - Stealth's quiet work, from the plate to the ladder.
 *
 * `move(def)` is what the drum and the trigger fire (through `quietMoveRef`,
 * since they are declared before this). It makes a request - who, which
 * plate, how hard they set it - and then:
 *
 * - whoever directs (the GM, or anyone outside a session) resolves it here;
 * - a player's goes to the GM as an ask, and waits in `requests` on the
 *   GM's screen until they Roll it or dismiss it.
 *
 * Resolving a request rolls the plate's test as a SECRET called test against
 * the sharpest of the opposition - the result is the GM's alone, by the
 * same road as any secret result - and sets a proposal: what the outcome
 * suggests for the ladder. Nothing moves until the GM accepts it (or presses
 * a rung, or leaves it). Go loud has nothing to roll: it proposes Alarm.
 *
 * An ATTACK made while the table is in Stealth is a request too
 * (`attackHeard`, through `attackHeardRef`): nothing is rolled, and its
 * proposal follows the weapon's noise, whether it landed, and who of the
 * opposition is still standing when the GM reads it.
 *
 * @param {object} s - the shell scope: quietMoveRef, attackHeardRef, stealth,
 *   direction, roster, actor, asks, notch, setNotch, unnaturalDosMode,
 *   postSecret, pushToast.
 * @returns {{ requests: {id, request, line, rolls}[], proposal: {title, rung, reason}|null, roll, dismiss, accept, leave, propose }}
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { actionById } from '../engine/actions/catalog';
import { NOTCH_AT_REST } from '../engine/actions/difficultyNotch';
import { resolveTestCall } from '../engine/narrative/callForTest';
import { layoutOf } from '../engine/phase/layout';
import { isInTheFight } from '../engine/roster/inTheFight';
import { RUNG_LABELS } from '../engine/stealth/ladder';
import { DEFAULT_SIGNATURE_REACH, heardAs, signatureOf } from '../engine/stealth/noise';
import { sharpest, watchers } from '../engine/stealth/opposition';
import { afterAttack, proposeFor } from '../engine/stealth/proposals';
import {
  QUIET_ASK,
  attackAskOf,
  attackLine,
  outcomeOf,
  quietAskLine,
  quietAskOf,
  quietBeatLine,
  quietCall,
  readQuietAsk,
} from '../engine/stealth/quietMove';
import { infoLine } from './shellCombatant';

/** Go loud: nothing to roll. The proposal is the alarm itself. */
function loudProposal(request, def, state) {
  const title = quietAskLine(request, def);
  if (state.rung === 'alarm') return { title, rung: 'alarm', reason: 'The ladder is already at Alarm.' };
  return { title, rung: 'alarm', reason: `${request.name} gives up on quiet. Proposed: ${RUNG_LABELS.alarm}.` };
}

/**
 * Roll a request's test and say what it proposes. The entry goes to the GM
 * alone (`postSecret`). A plate that could not be rolled (an untrained
 * advanced skill) proposes nothing and says why.
 */
function rolledProposal(request, def, now) {
  const { stealth, roster, notch, unnaturalDosMode, postSecret } = now;
  // The sharpest of those the MOVER is hiding from. On the GM's screen the opposition was worked out
  // without knowing who would move, and until the party is named it includes the mover themselves.
  const watching = stealth.opposition.filter((c) => c.id !== request.actorId);
  const call = quietCall(def, request, sharpest(watching, stealth.state)?.id ?? null, notch?.theirs ?? 0);
  const [result] = resolveTestCall(call, roster, { unnaturalDosMode });
  const title = quietAskLine(request, def);
  if (result) postSecret(`${def.name} - ${result.entry.summary}`);
  showTheGesture(request, def, now);
  const outcome = outcomeOf(result);
  if (!outcome) return { title, rung: stealth.state.rung, reason: result?.entry.summary ?? 'They are no longer here.' };
  return { title, ...proposeFor(def.quiet.move, outcome, stealth.state) };
}

/**
 * The quiet move's gesture, for every screen's map (plan design-gaps, Z1): a public story line with its beat - never
 * the roll's result. None for a mover the GM has hidden from the players; a target hidden from them is left out,
 * and the gesture plays on the mover alone.
 */
function showTheGesture(request, def, now) {
  const isHidden = now.hidden?.isHidden;
  if (isHidden?.(request.actorId)) return;
  const toward = request.targetId && isHidden?.(request.targetId) ? { ...request, targetId: undefined } : request;
  const line = quietBeatLine(toward, def, Date.now());
  if (!line) return;
  now.addNarrativeRef?.current?.(request.actorId, { ...infoLine(line.text), lane: 'story', beat: line.beat });
}

/**
 * An attack's proposal: by the weapon's noise, whether it landed, and who
 * of the opposition is standing NOW - read when it is shown, not when the
 * attack was made, since the hits are still landing then.
 */
function attackProposal(request, stealth) {
  const standing = stealth.opposition.filter(isInTheFight).map((c) => c.id);
  return { title: attackLine(request), ...afterAttack({ ...request.attack, standing }, stealth.state) };
}

/** The proposal a plate's request leads to: a rolled test, or going loud. */
function plateProposal(request, now) {
  const def = actionById(request.moveId);
  if (!def) return null;
  return def.quiet ? rolledProposal(request, def, now) : loudProposal(request, def, now.stealth.state);
}

/** How a request reads on the GM's screen, and whether answering it rolls dice. Null for one this build cannot read. */
function described(request) {
  if (!request) return null;
  if (request.attack) return { line: attackLine(request), rolls: false };
  const def = actionById(request.moveId);
  return def ? { line: quietAskLine(request, def), rolls: !!def.quiet } : null;
}

/** The requests waiting on this screen: asks of the quiet kind addressed here, as `{ id, request, line, rolls }`. */
function requestsFrom(incoming) {
  return incoming
    .filter((a) => a.kind === QUIET_ASK)
    .map((a) => ({ id: a.id, request: readQuietAsk(a.payload) }))
    .map((r) => ({ ...r, ...described(r.request) }))
    .filter((r) => r.line);
}

/** Is the ladder listening for attacks? While the table is in a phase that wears it, and below Alarm. */
function ladderListens(now) {
  const { frame } = now.direction;
  return !!frame && layoutOf(frame.phase).wearsRung && now.stealth.state.rung !== 'alarm';
}

/**
 * What is waiting to be settled on this screen, and the two ways it gets
 * there: a rolled proposal (fixed when rolled), or an attack that was heard
 * (its proposal derived each render against the roster as it stands).
 */
function useSettling(live) {
  const [rolled, setRolled] = useState(null);
  const [heard, setHeard] = useState(null);
  const resolve = useCallback(
    (request) => {
      const now = live.current;
      // One thing at a time: whatever was up is replaced by what just happened.
      setHeard(request.attack ? request : null);
      setRolled(request.attack ? null : plateProposal(request, now));
      if (!request.attack) now.setNotch?.(NOTCH_AT_REST);
    },
    [live],
  );
  const settle = useCallback(() => {
    setRolled(null);
    setHeard(null);
  }, []);
  /** Put a proposal up that was worked out elsewhere (somebody looked at the approach). */
  const propose = useCallback((proposal) => {
    setHeard(null);
    setRolled(proposal);
  }, []);
  const shown = rolled ?? (heard ? attackProposal(heard, live.current.stealth) : null);
  return { shown, resolve, settle, propose };
}

/**
 * The two things this screen reports: a pressed plate (`move`) and an
 * attack that was made (`attackHeard`). Each becomes a request, resolved
 * here by whoever directs and sent to the GM by a player.
 */
function useReports(live, resolve) {
  /** Put a request where it is resolved. Says whether that was here. */
  const send = useCallback(
    (request) => {
      const now = live.current;
      if (now.stealth.canDirect) resolve(request);
      else now.asks.ask(QUIET_ASK, 'gm', request);
      return now.stealth.canDirect;
    },
    [live, resolve],
  );
  const move = useCallback(
    (def) => {
      const now = live.current;
      if (!def || !now.actor) return;
      if (send(quietAskOf(def, now.actor, now.notch?.mine ?? 0, now.activeTab))) return;
      // Sent to the GM: the notch was for this, and the player is told it went.
      now.setNotch?.(NOTCH_AT_REST);
      now.pushToast?.(`Asked the GM: ${def.name}.`);
    },
    [live, send],
  );
  /** An attack was made: the ladder hears the weapon if somebody of the opposition stands within its reach. */
  const attackHeard = useCallback(
    ({ hit, slot }) => {
      const now = live.current;
      if (now.actor && ladderListens(now)) send(attackAskOf(now.actor, heardBy(now, slot), hit, signatureOf(slot)));
    },
    [live, send],
  );
  return { move, attackHeard };
}

/**
 * How the ladder hears this attack (plan armoury, the ruling 2026-10-19): REACH ALONE DECIDES WHO HEARS. A weapon
 * is loud for the ladder when a watcher of the opposition stands within its Signature's reach at this table, silent
 * when none does; with nowhere to measure from, its step decides alone (`heardAs`).
 */
function heardBy(now, slot) {
  // The positions the rules read: none when the table says positions do not count, and then the step decides alone.
  const at = (id) => now.ruleTokens?.[id] ?? null;
  const listening = watchers(
    (now.stealth?.opposition ?? []).filter((c) => c.id !== now.actor.id),
    now.stealth.state,
  );
  const listeners = listening.map((c) => at(c.id)).filter(Boolean);
  return heardAs(signatureOf(slot), now.signatureReach ?? DEFAULT_SIGNATURE_REACH, at(now.actor.id), listeners);
}

/** The GM's two answers to a player's request: resolve it here (the ask is answered, so it leaves both screens), or dismiss it. */
function useAnswers(live, resolve) {
  // Answered here already. The request stays on screen until the answer has made the round trip,
  // and a second press in that gap must not roll it again.
  const answered = useRef(new Set());
  const roll = useCallback(
    (id) => {
      const waiting = requestsFrom(live.current.asks.incoming).find((r) => r.id === id);
      if (!waiting || answered.current.has(id)) return;
      answered.current.add(id);
      live.current.asks.answer(id, true);
      resolve(waiting.request);
    },
    [live, resolve],
  );
  const dismiss = useCallback((id) => live.current.asks.cancel(id), [live]);
  return { roll, dismiss };
}

export default function useShellQuietMoves(s) {
  const live = useRef(s);
  live.current = s;
  const { shown, resolve, settle, propose } = useSettling(live);
  // As the verbs read it: accepting moves the ladder, which no state updater may do.
  const shownRef = useRef(shown);
  shownRef.current = shown;

  const { move, attackHeard } = useReports(live, resolve);
  // Read at press time by the drum, the trigger and the attack, which are declared before this hook.
  s.quietMoveRef.current = move;
  s.attackHeardRef.current = attackHeard;

  const requests = useMemo(() => requestsFrom(s.asks.incoming), [s.asks.incoming]);
  const { roll, dismiss } = useAnswers(live, resolve);
  const accept = useCallback(() => {
    const was = shownRef.current;
    if (was) live.current.stealth.setRung(was.rung);
    settle();
  }, [settle]);

  return useMemo(
    () => ({ requests, proposal: shown, roll, dismiss, accept, leave: settle, propose }),
    [requests, shown, roll, dismiss, accept, settle, propose],
  );
}
